import { useRouter } from 'next/router';
import React, { type CSSProperties, type FC, type PropsWithChildren, createContext, useContext, useMemo } from 'react';

import deserializeQueryStringItem from '@/helpers/deserializeQueryStringItem';
import COLORS, { type COLOR_KEYS } from '@/styles/colors';
import { type CreatorUiApi, useCreatorUi } from '@/tsClient/getCreatorUI';

type CreatorColors = { [K in COLOR_KEYS]?: string };
type CreatorUiApiResponse = CreatorUiApi['response'];
type CreatorUi = Partial<CreatorUiApiResponse> & Pick<CreatorUiApiResponse, 'slug'> & { colors: CreatorColors };

type CreatorSlug = string | null;

type CreatorUiContextValue = {
  isLoaded: boolean;
  creatorSlug: CreatorSlug;
  uiData: CreatorUi;
};

interface CreatorColorCssProperties extends CSSProperties {
  '--color-primary'?: string;
  '--primary'?: string;
  '--primary-gradient'?: string;
  '--shadow-primary'?: string;
}

const defaultCreatorUi: CreatorUi = {
  colors: {},
  slug: '',
};

const getStyles = (uiData: Partial<CreatorUi>): CreatorColorCssProperties => {
  const newStyle: CreatorColorCssProperties = {};
  // If this is updated, make the same change to NOODLE_STYLE below.
  if (uiData.colors?.primary) {
    newStyle['--color-primary'] = uiData.colors.primary;
    newStyle['--primary'] = uiData.colors.primary;
    newStyle['--shadow-primary'] = `0px 8px 24px ${uiData.colors.primary}50`;
    newStyle['--primary-gradient'] =
      `linear-gradient(180deg, ${uiData.colors.primary}00 48%, color-mix(in srgb, black 20%, ${uiData.colors.primary}) 96%)`;
  }
  return newStyle;
};

export const NOODLE_THEME_STYLE = getStyles({
  colors: {
    primary: 'var(--color-noodle)',
  },
});

const defaultValue = {
  creatorSlug: null,
  isLoaded: false,
  uiData: defaultCreatorUi,
} satisfies CreatorUiContextValue;

const CreatorUIContext = createContext<CreatorUiContextValue>(defaultValue);

const creatorUiProviderRootAttrs = { className: 'contents', id: 'creator-ui-provider-style-wrapper', role: 'none' };

const CreatorUiProviderImpl: FC<PropsWithChildren<{ creatorSlug: string }>> = (props) => {
  const { data: creatorUi = defaultCreatorUi, isLoading } = useCreatorUi(props);
  const value = useMemo<CreatorUiContextValue>(
    () => ({
      ...defaultValue,
      creatorSlug: props.creatorSlug,
      isLoaded: !isLoading,
      uiData: {
        ...creatorUi,
        colors: {
          ...COLORS,
          ...(creatorUi.primaryColour && { primary: creatorUi.primaryColour.hex }),
        },
      },
    }),
    [props.creatorSlug, isLoading, creatorUi],
  );
  return (
    <CreatorUIContext.Provider value={value}>
      <div
        {...creatorUiProviderRootAttrs}
        style={isLoading ? { display: 'none' } : creatorUi.primaryColour ? getStyles(value.uiData) : undefined}
      >
        {props.children}
      </div>
    </CreatorUIContext.Provider>
  );
};

const CreatorUiProviderNoOp: FC<PropsWithChildren> = ({ children }) => (
  <CreatorUIContext.Provider value={defaultValue}>
    <div {...creatorUiProviderRootAttrs}>{children}</div>
  </CreatorUIContext.Provider>
);

const CreatorUIProvider: FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  const creatorSlug = deserializeQueryStringItem(router.query.creatorSlug) || null;

  return creatorSlug ? (
    <CreatorUiProviderImpl creatorSlug={creatorSlug}>{children}</CreatorUiProviderImpl>
  ) : (
    <CreatorUiProviderNoOp>{children}</CreatorUiProviderNoOp>
  );
};

export const useCreatorUI = (): CreatorUi | null => {
  const { uiData, isLoaded } = useContext(CreatorUIContext);
  return isLoaded ? uiData : null;
};

export const uiDataToStyle = getStyles;

export type { CreatorUi };

export default CreatorUIProvider;
